import { useState, useEffect } from 'react';
import { Pie, PieChart, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { ChevronDown, ChevronUp, HelpCircle, Globe } from 'lucide-react';

export default function LoanVisualizer() {
  // SEO metadata
  const pageTitle = "EMI Calculator - Loan Calculator & EMI Visualization Tool";
  const pageDescription = "Free online EMI calculator to calculate your loan EMI, total interest payable, and visualize your loan repayment. Instant results for home loans, car loans, personal loans.";
  const keywords = "emi calculator, loan calculator, home loan emi, car loan calculator, personal loan emi calculator, loan visualizer, interest calculator, loan repayment calculator";

  useEffect(() => {
    // Update document title
    document.title = pageTitle;

    // Add meta tags programmatically if needed
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', pageDescription);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = pageDescription;
      document.head.appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = keywords;
      document.head.appendChild(meta);
    }
  }, []);

  const [principalAmount, setPrincipalAmount] = useState(3500000);
  const [interestRate, setInterestRate] = useState(8.8);
  const [loanTenure, setLoanTenure] = useState(20);
  const [annualLumpsum, setAnnualLumpsum] = useState(0);
  const [emiStepUpRate, setEmiStepUpRate] = useState(0);
  const [emi, setEmi] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationVisible, setAmortizationVisible] = useState(false);
  const [yearlyAmortization, setYearlyAmortization] = useState([]);
  const [activeTab, setActiveTab] = useState('calculator'); // 'calculator' or 'details'
  const [showLumpsumTooltip, setShowLumpsumTooltip] = useState(false);
  const [showStepUpTooltip, setShowStepUpTooltip] = useState(false);
  const [currencyType, setCurrencyType] = useState('INR');
  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const currencies = {
        'INR': { symbol: '₹', code: 'INR' },
        'USD': { symbol: '$', code: 'USD' },
        'AED': { symbol: 'د.إ', code: 'AED' },
        'GBP': { symbol: '£', code: 'GBP' },
        'SGD': { symbol: 'S$', code: 'SGD' },
        'EUR': { symbol: '€', code: 'EUR' },
        'CAD': { symbol: 'C$', code: 'CAD' },
        'AUD': { symbol: 'A$', code: 'AUD' },
        'RMB': { symbol: '¥', code: 'RMB' }
      };

  const currencyFormatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currencies[currencyType].code,
    maximumFractionDigits: 0
  });



  useEffect(() => {
    calculateLoan();
  }, [principalAmount, interestRate, loanTenure, annualLumpsum, emiStepUpRate]);

  const calculateLoan = () => {
    const p = principalAmount;
    const r = interestRate / 12 / 100;
    const n = loanTenure * 12;
    const stepUpRate = emiStepUpRate / 100;

    // Calculate initial EMI
    const calculatedEmi = p * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
    setEmi(calculatedEmi);

    // Generate amortization schedule with lumpsum and EMI step-up
    generateAmortizationSchedule(p, r, n, calculatedEmi, annualLumpsum, stepUpRate);
  };

  const generateAmortizationSchedule = (principal, monthlyRate, months, initialMonthlyPayment, yearlyLumpsum, stepUpRate) => {
    let balance = principal;
    let yearlyData = [];
    let totalPaidAmount = 0;
    let totalInterestPaid = 0;

    let yearInterest = 0;
    let yearPrincipal = 0;
    let currentMonthlyPayment = initialMonthlyPayment;
    let currentYear = 1;

    for (let month = 1; month <= months; month++) {
      // Apply EMI step-up at the beginning of each year (except first year)
      if (month > 12 && month % 12 === 1 && stepUpRate > 0) {
        currentMonthlyPayment = currentMonthlyPayment * (1 + stepUpRate);
      }

      // Calculate interest and principal for this month
      const interestPayment = balance * monthlyRate;
      const principalPayment = Math.min(balance, currentMonthlyPayment - interestPayment);

      yearInterest += interestPayment;
      yearPrincipal += principalPayment;

      // Update balance
      balance -= principalPayment;

      // Track totals
      totalPaidAmount += (principalPayment + interestPayment);
      totalInterestPaid += interestPayment;

      // Apply lumpsum payment at the end of each year
      if (month % 12 === 0 && yearlyLumpsum > 0) {
        const lumpsumPayment = Math.min(balance, yearlyLumpsum);
        if (lumpsumPayment > 0) {
          balance -= lumpsumPayment;
          yearPrincipal += lumpsumPayment;
          totalPaidAmount += lumpsumPayment;
        }
      }

      // Store yearly data
      if (month % 12 === 0 || month === months) {
        const year = Math.ceil(month / 12);
        yearlyData.push({
          year: year,
          principalPaid: yearPrincipal,
          interestPaid: yearInterest,
          balance: balance > 0 ? balance : 0,
          emi: month <= 12 ? initialMonthlyPayment : currentMonthlyPayment,
          lumpsum: year < Math.ceil(months / 12) ? yearlyLumpsum : 0
        });

        yearInterest = 0;
        yearPrincipal = 0;
        currentYear++;
      }
    }

    // Update state with calculated values
    setYearlyAmortization(yearlyData);
    setTotalAmount(totalPaidAmount);
    setTotalInterest(totalInterestPaid);
  };

  const handlePrincipalChange = (e) => {
    const value = parseFloat(e.target.value.replace(/,/g, ''));
    if (!isNaN(value)) {
      setPrincipalAmount(value);
    }
  };

  const handleInterestChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 0 && value <= 100) {
      setInterestRate(value);
    }
  };

  const handleTenureChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0) {
      setLoanTenure(value);
    }
  };

  const handleAnnualLumpsumChange = (e) => {
    const value = parseFloat(e.target.value.replace(/,/g, ''));
    if (!isNaN(value) && value >= 0) {
      setAnnualLumpsum(value);
    }
  };

  const handleEmiStepUpChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 0 && value <= 100) {
      setEmiStepUpRate(value);
    }
  };

  const pieData = [
    { name: 'Principal', value: principalAmount },
    { name: 'Interest', value: totalInterest }
  ];

  const COLORS = ['#E2E8F0', '#38BDF8'];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="px-4 py-6 mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-6 text-center relative">
            <div className="absolute right-0 top-0">
              <div className="relative">
                <button
                  onClick={() => setShowCurrencyMenu(!showCurrencyMenu)}
                  className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg min-w-[110px]"
                >
                  <Globe className="h-4 w-4 mr-2" />
                  <span>{currencies[currencyType].symbol} {currencyType}</span>
                </button>

                {showCurrencyMenu && (
                  <div className="absolute right-0 top-full mt-2 w-40 bg-white shadow-lg rounded-lg z-10">
                    {Object.keys(currencies).map((currency) => (
                      <button
                        key={currency}
                        onClick={() => {
                          setCurrencyType(currency);
                          setShowCurrencyMenu(false);
                        }}
                        className="block w-full text-left px-4 py-2 hover:bg-blue-50 text-sm"
                      >
                        {currencies[currency].symbol} {currency}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {!isMobile && (
                <div>
                    <h1 className="text-2xl font-bold text-blue-900 mb-2">Visualize your EMIs</h1>
                    <p className="text-blue-600">Plan your loan and see exactly what you'll pay</p>
                </div>
            )}

        </div>

        {/* Results Card */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
            <div className="mb-4 sm:mb-0">
              <h2 className="text-sm text-blue-600 font-medium">Your Monthly EMI</h2>
              <div className="text-3xl font-bold text-blue-900">
                {currencyFormatter.format(emi)}
              </div>
            </div>

            <div className="w-full sm:w-40 h-40">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={0}
                    dataKey="value"
                    label={false}
                    startAngle={90}
                    endAngle={-270}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => currencyFormatter.format(value)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-sm text-blue-700 mb-1">Principal</div>
              <div className="text-lg font-semibold">{currencyFormatter.format(principalAmount)}</div>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-sm text-blue-700 mb-1">Interest</div>
              <div className="text-lg font-semibold">{currencyFormatter.format(totalInterest)}</div>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-sm text-blue-700 mb-1">Total Amount</div>
              <div className="text-lg font-semibold">{currencyFormatter.format(totalAmount)}</div>
            </div>
          </div>

          <div className="flex justify-center space-x-2 mb-6">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-gray-200 rounded-full mr-2"></div>
              <span className="text-xs text-gray-600">Principal</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-400 rounded-full mr-2"></div>
              <span className="text-xs text-gray-600">Interest</span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex mb-6">
          <button
            onClick={() => setActiveTab('calculator')}
            className={`flex-1 py-3 text-center text-sm font-medium rounded-l-lg transition-colors ${
              activeTab === 'calculator'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Calculate
          </button>
          <button
            onClick={() => setActiveTab('details')}
            className={`flex-1 py-3 text-center text-sm font-medium rounded-r-lg transition-colors ${
              activeTab === 'details'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            About EMI
          </button>
        </div>

        {activeTab === 'calculator' ? (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
            {/* Principal Amount Input */}
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <label htmlFor="principal-amount" className="text-sm font-medium text-gray-700">
                  Loan amount
                </label>
                <span className="text-sm font-medium text-blue-700">
                  {currencyFormatter.format(principalAmount)}
                </span>
              </div>
              <div className="relative mb-2">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                <input
                  id="principal-amount"
                  type="text"
                  value={currencyFormatter.format(principalAmount)}
                  onChange={handlePrincipalChange}
                  className="w-full py-3 pl-8 pr-3 bg-blue-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
                  aria-label="Principal loan amount"
                />
              </div>
              <input
                type="range"
                min="100000"
                max="20000000"
                step="100000"
                value={principalAmount}
                onChange={(e) => setPrincipalAmount(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                aria-label="Principal amount slider"
              />
            </div>

            {/* Interest Rate Input */}
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <label htmlFor="interest-rate" className="text-sm font-medium text-gray-700">
                  Rate of interest (p.a)
                </label>
                <span className="text-sm font-medium text-blue-700">
                  {interestRate}%
                </span>
              </div>
              <div className="relative mb-2">
                <input
                  id="interest-rate"
                  type="number"
                  value={interestRate}
                  step="0.1"
                  onChange={handleInterestChange}
                  className="w-full py-3 px-3 bg-blue-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
                  aria-label="Annual interest rate percentage"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
              </div>
              <input
                type="range"
                min="2"
                max="20"
                step="0.1"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                aria-label="Interest rate slider"
              />
            </div>

            {/* Loan Tenure Input */}
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <label htmlFor="loan-tenure" className="text-sm font-medium text-gray-700">
                  Loan tenure
                </label>
                <span className="text-sm font-medium text-blue-700">
                  {loanTenure} Year{loanTenure !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="relative mb-2">
                <input
                  id="loan-tenure"
                  type="number"
                  value={loanTenure}
                  onChange={handleTenureChange}
                  className="w-full py-3 px-3 bg-blue-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
                  aria-label="Loan tenure in years"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">Yr</span>
              </div>
              <input
                type="range"
                min="1"
                max="30"
                value={loanTenure}
                onChange={(e) => setLoanTenure(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                aria-label="Loan tenure slider"
              />
            </div>

            {/* Annual Lumpsum Payment Input */}
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <div className="flex items-center">
                  <label htmlFor="annual-lumpsum" className="text-sm font-medium text-gray-700 mr-1">
                    Annual lumpsum payment
                  </label>
                  <button
                    onClick={() => setShowLumpsumTooltip(!showLumpsumTooltip)}
                    className="text-blue-400 hover:text-blue-600"
                    aria-label="Show info about lumpsum payment"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </button>
                </div>
                <span className="text-sm font-medium text-blue-700">
                  {currencyFormatter.format(annualLumpsum)}
                </span>
              </div>
              {showLumpsumTooltip && (
                <div className="mb-2 p-2 bg-blue-50 rounded-md text-xs text-gray-700">
                  This is an additional payment made once every year to reduce your principal faster. It helps you save on interest and reduce loan tenure.
                </div>
              )}
              <div className="relative mb-2">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                <input
                  id="annual-lumpsum"
                  type="text"
                  value={annualLumpsum.toLocaleString()}
                  onChange={handleAnnualLumpsumChange}
                  className="w-full py-3 pl-8 pr-3 bg-blue-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
                  aria-label="Annual lumpsum payment amount"
                />
              </div>
              <input
                type="range"
                min="0"
                max={principalAmount / 2}
                step={10000}
                value={annualLumpsum}
                onChange={(e) => setAnnualLumpsum(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                aria-label="Annual lumpsum payment slider"
              />
            </div>

            {/* EMI Step-Up Input */}
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <div className="flex items-center">
                  <label htmlFor="emi-step-up" className="text-sm font-medium text-gray-700 mr-1">
                    EMI step-up rate
                  </label>
                  <button
                    onClick={() => setShowStepUpTooltip(!showStepUpTooltip)}
                    className="text-blue-400 hover:text-blue-600"
                    aria-label="Show info about EMI step-up"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </button>
                </div>
                <span className="text-sm font-medium text-blue-700">
                  {emiStepUpRate}%
                </span>
              </div>
              {showStepUpTooltip && (
                <div className="mb-2 p-2 bg-blue-50 rounded-md text-xs text-gray-700">
                  This increases your EMI by a specified percentage each year. It's useful if you expect your income to rise over time, allowing you to repay faster.
                </div>
              )}
              <div className="relative mb-2">
                <input
                  id="emi-step-up"
                  type="number"
                  value={emiStepUpRate}
                  onChange={handleEmiStepUpChange}
                  className="w-full py-3 px-3 bg-blue-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
                  aria-label="EMI step-up percentage"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
              </div>
              <input
                type="range"
                min="0"
                max="20"
                step="1"
                value={emiStepUpRate}
                onChange={(e) => setEmiStepUpRate(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                aria-label="EMI step-up rate slider"
              />
            </div>

            {/* Amortization Details */}
            <div className="mt-6">
              <button
                onClick={() => setAmortizationVisible(!amortizationVisible)}
                className="flex items-center justify-between w-full py-3 px-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                aria-expanded={amortizationVisible}
                aria-controls="amortization-table"
              >
                <span className="text-sm font-medium text-blue-700">Repayment Schedule</span>
                {amortizationVisible ? (
                  <ChevronUp className="h-5 w-5 text-blue-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-blue-500" />
                )}
              </button>

              {amortizationVisible && (
                <div id="amortization-table" className="mt-4 overflow-x-auto bg-white rounded-lg">
                  <table className="w-full border-collapse text-sm">
                    <caption className="sr-only">Yearly Amortization Schedule</caption>
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th scope="col" className="py-3 px-4 text-left font-medium text-gray-700">Year</th>
                        <th scope="col" className="py-3 px-4 text-right font-medium text-gray-700">EMI</th>
                        <th scope="col" className="py-3 px-4 text-right font-medium text-gray-700">Principal Paid</th>
                        <th scope="col" className="py-3 px-4 text-right font-medium text-gray-700">Interest Paid</th>
                        <th scope="col" className="py-3 px-4 text-right font-medium text-gray-700">Lumpsum</th>
                        <th scope="col" className="py-3 px-4 text-right font-medium text-gray-700">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yearlyAmortization.map((row, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-blue-50' : 'bg-white'}>
                          <td className="py-3 px-4 text-gray-800">{row.year}</td>
                          <td className="py-3 px-4 text-right text-gray-800">{currencyFormatter.format(row.emi)}</td>
                          <td className="py-3 px-4 text-right text-gray-800">{currencyFormatter.format(row.principalPaid)}</td>
                          <td className="py-3 px-4 text-right text-blue-600">{currencyFormatter.format(row.interestPaid)}</td>
                          <td className="py-3 px-4 text-right text-green-600">{currencyFormatter.format(row.lumpsum)}</td>
                          <td className="py-3 px-4 text-right text-gray-800">{currencyFormatter.format(row.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
            <div className="prose max-w-none">
              <h2 id="about-emi-calculator" className="text-xl font-semibold text-blue-900 mb-4">About EMI Calculator</h2>
              <p className="text-gray-700">Our EMI (Equated Monthly Installment) calculator helps you plan loan repayments effectively. Whether you're considering a home loan, car loan, or personal loan, this calculator gives you an accurate estimate of your monthly payments.</p>

              <h3 id="how-emi-calculator-works" className="text-lg font-medium text-blue-800 mt-6 mb-3">How EMI Calculation Works</h3>
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <p className="font-mono text-blue-700 text-sm mb-2">EMI = [P x R x (1+R)^N]/[(1+R)^N-1]</p>
                <p className="text-sm text-gray-700 mb-2">Where:</p>
                <ul className="list-disc pl-5 text-sm text-gray-700">
                  <li><strong>P</strong> is the principal loan amount</li>
                  <li><strong>R</strong> is the interest rate per month</li>
                  <li><strong>N</strong> is the loan tenure in months</li>
                </ul>
              </div>

              <h3 id="benefits-of-using-emi-calculator" className="text-lg font-medium text-blue-800 mt-6 mb-3">Benefits of Using This Calculator</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 p-1 rounded-full mr-2 text-xs">✓</span>
                  <span><strong>Plan your finances</strong>: Understand your monthly commitments</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 p-1 rounded-full mr-2 text-xs">✓</span>
                  <span><strong>Compare options</strong>: Find the best loan terms for you</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 p-1 rounded-full mr-2 text-xs">✓</span>
                  <span><strong>See interest costs</strong>: Visualize your full loan payment</span>
                </li>
              </ul>

              <h3 id="faq-emi-calculator" className="text-lg font-medium text-blue-800 mt-6 mb-3">Frequently Asked Questions</h3>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-700">What is EMI?</h4>
                  <p className="text-sm text-gray-700">EMI (Equated Monthly Installment) is the fixed amount you pay to the lender each month, combining principal and interest.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-700">How do annual lumpsum payments help?</h4>
                  <p className="text-sm text-gray-700">Making additional annual payments reduces your principal faster, saving significant interest over the loan term and potentially shortening your repayment period.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-700">What is EMI step-up?</h4>
                  <p className="text-sm text-gray-700">EMI step-up allows you to start with lower EMIs that increase annually. This is ideal for young professionals who expect their income to grow over time.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-700">Does tenure affect total interest?</h4>
                  <p className="text-sm text-gray-700">Yes, a longer tenure reduces monthly EMI but increases total interest paid. A shorter tenure means higher EMIs but lower overall interest.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}